import React, { useState, useEffect, useMemo } from 'react';
import {
    Users, TrendingUp, Clock, AlertCircle, Ticket, Gift, Bell, Utensils,
    Package, ShoppingBag, CreditCard, AlertTriangle, ArrowRight, TrendingDown,
    Activity, ArrowUpRight, Wallet, Star, ChevronRight, Zap, BarChart3,
    UserPlus, Eye, Receipt, QrCode, ScanLine, UserCheck
} from 'lucide-react';

import { supabase } from '../../supabase/config';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useCustomers } from '../../context/CustomerContext';
import { useNavigate } from 'react-router-dom';
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
    Title, Tooltip, Legend, Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import { anonymizeName } from '../../utils/customer';
import SetupWizard from '../../components/dashboard/SetupWizard';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

const Dashboard = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { userData, tenantData, effectiveTenantId } = useAuth();
    const { customers, loading: customersLoading } = useCustomers();

    const [activities, setActivities] = useState([]);
    const [orders, setOrders] = useState([]);
    const [promotions, setPromotions] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [lowStocks, setLowStocks] = useState([]);
    const [todaySales, setTodaySales] = useState({ total: 0, count: 0 });
    const [currentTime, setCurrentTime] = useState(new Date());
    const [qrPeriod, setQrPeriod] = useState('week');

    useEffect(() => {
        const timer = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        if (!effectiveTenantId) return;
        const subscriptions = [];

        const fetchInitialData = async () => {
            const { data: actData } = await supabase
                .from('activities').select('*').eq('tenant_id', effectiveTenantId)
                .order('timestamp', { ascending: false }).limit(8);
            setActivities((actData || []).map(r => ({ ...r, tenantId: r.tenant_id, userEmail: r.user_email })));

            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const { data: salesData } = await supabase
                .from('sales').select('*').eq('tenant_id', effectiveTenantId)
                .gte('timestamp', today.toISOString()).order('timestamp', { ascending: false });
            const orderList = (salesData || []).map(r => ({
                ...r, tenantId: r.tenant_id, totalAmount: r.amount,
                tableNumber: null, customerName: r.customer_name, customerId: r.customer_id
            }));
            setOrders(orderList);
            const total = orderList.reduce((acc, c) => acc + (c.totalAmount || 0), 0);
            setTodaySales({ total, count: orderList.length });

            const { data: promoData } = await supabase
                .from('discounts').select('*').eq('tenant_id', effectiveTenantId).eq('is_active', true);
            setPromotions((promoData || []).map(r => ({ ...r, tenantId: r.tenant_id, isActive: r.is_active, isReward: r.is_reward })));

            const { data: notifData } = await supabase
                .from('notifications').select('*').eq('tenant_id', effectiveTenantId)
                .order('created_at', { ascending: false }).limit(3);
            setNotifications((notifData || []).map(r => ({ ...r, tenantId: r.tenant_id, createdAt: r.created_at })));

            const { data: stockData } = await supabase
                .from('product_stocks').select('*').eq('tenant_id', effectiveTenantId).maybeSingle();
            if (stockData) {
                const stocks = stockData.stocks || {};
                setLowStocks(Object.entries(stocks).filter(([_, qty]) => qty !== -1 && qty <= 5).map(([id, qty]) => ({ id, qty })));
            }
        };

        fetchInitialData();

        const ordersChannel = supabase.channel('dashboard-orders')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'orders', filter: `tenant_id=eq.${effectiveTenantId}` }, () => fetchInitialData())
            .subscribe();
        subscriptions.push(ordersChannel);

        const activitiesChannel = supabase.channel('dashboard-activities')
            .on('postgres_changes', { event: '*', schema: 'public', table: 'activities', filter: `tenant_id=eq.${effectiveTenantId}` }, () => fetchInitialData())
            .subscribe();
        subscriptions.push(activitiesChannel);

        return () => subscriptions.forEach(ch => supabase.removeChannel(ch));
    }, [effectiveTenantId]);

    const primaryColor = '#6f4e37';

    const chartData = useMemo(() => {
        const now = new Date().getHours();
        const hours = Array.from({ length: 24 }, (_, i) => `${i.toString().padStart(2, '0')}:00`);
        const revenueByHour = new Array(24).fill(0);
        orders.forEach(order => {
            const hour = new Date(order.timestamp).getHours();
            revenueByHour[hour] += (order.totalAmount || 0);
        });
        return {
            labels: hours.slice(0, now + 1),
            datasets: [{
                label: 'Ciro (₺)', data: revenueByHour.slice(0, now + 1),
                fill: true, borderColor: primaryColor, backgroundColor: primaryColor + '12',
                tension: 0.4, pointRadius: revenueByHour.slice(0, now + 1).length > 12 ? 0 : 4,
                pointBackgroundColor: primaryColor, borderWidth: 2,
            }]
        };
    }, [orders, primaryColor]);

    // Customer insights
    const customerInsights = useMemo(() => {
        if (!customers || customers.length === 0) return null;
        const now = new Date();
        const thirtyDaysAgo = new Date(now - 30 * 24 * 60 * 60 * 1000);
        const newCustomers = customers.filter(c => c.createdAt && new Date(c.createdAt) >= thirtyDaysAgo).length;
        const activeCustomers = customers.filter(c => c.lastVisit && new Date(c.lastVisit) >= thirtyDaysAgo).length;
        const totalSpent = customers.reduce((acc, c) => acc + (c.totalSpent || 0), 0);
        return { newCustomers, activeCustomers, totalSpent };
    }, [customers]);

    const qrStats = useMemo(() => {
        if (!customers || customers.length === 0) return { totalScans: 0, newMembers: 0, topCustomers: [] };
        const now = new Date();
        let periodStart;
        if (qrPeriod === 'today') {
            periodStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        } else if (qrPeriod === 'week') {
            periodStart = new Date(now - 7 * 24 * 60 * 60 * 1000);
        } else {
            periodStart = new Date(now - 30 * 24 * 60 * 60 * 1000);
        }

        let totalScans = 0;
        const customerScans = [];

        customers.forEach(c => {
            const visits = c.visitTimestamps || c.visit_timestamps || [];
            const periodVisits = visits.filter(v => new Date(v) >= periodStart);
            totalScans += periodVisits.length;
            if (periodVisits.length > 0) {
                customerScans.push({
                    id: c.id,
                    name: c.firstName || c.first_name || c.name || 'Anonim',
                    scans: periodVisits.length,
                    lastVisit: periodVisits[periodVisits.length - 1]
                });
            }
        });

        const newMembers = customers.filter(c => {
            const createdAt = c.createdAt || c.created_at;
            return createdAt && new Date(createdAt) >= periodStart;
        }).length;

        const topCustomers = customerScans.sort((a, b) => b.scans - a.scans).slice(0, 5);
        const conversionRate = totalScans > 0 ? Math.round((newMembers / totalScans) * 100) : 0;

        return { totalScans, newMembers, conversionRate, topCustomers };
    }, [customers, qrPeriod]);

    const avgBasket = todaySales.count > 0 ? Math.round(todaySales.total / todaySales.count) : 0;

    const greeting = useMemo(() => {
        const h = new Date().getHours();
        if (h < 6) return 'İyi Geceler';
        if (h < 12) return 'Günaydın';
        if (h < 18) return 'İyi Günler';
        return 'İyi Akşamlar';
    }, []);


    const getActivityIcon = (action) => {
        if (!action) return Activity;
        const a = action.toLowerCase();
        if (a.includes('sipariş') || a.includes('order')) return ShoppingBag;
        if (a.includes('müşteri') || a.includes('customer')) return Users;
        if (a.includes('ödeme') || a.includes('payment')) return CreditCard;
        if (a.includes('indirim') || a.includes('kampanya')) return Ticket;
        if (a.includes('stok') || a.includes('stock')) return Package;
        return Activity;
    };

    const getActivityColor = (action) => {
        if (!action) return primaryColor;
        const a = action.toLowerCase();
        if (a.includes('sipariş') || a.includes('order')) return '#10b981';
        if (a.includes('müşteri') || a.includes('customer')) return '#3b82f6';
        if (a.includes('ödeme') || a.includes('payment')) return '#8b5cf6';
        if (a.includes('sil') || a.includes('delete')) return '#ef4444';
        return primaryColor;
    };

    const formatTimeAgo = (timestamp) => {
        if (!timestamp) return '';
        const diff = Date.now() - new Date(timestamp).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return 'Az önce';
        if (mins < 60) return `${mins} dk önce`;
        const hours = Math.floor(mins / 60);
        if (hours < 24) return `${hours} sa önce`;
        return new Date(timestamp).toLocaleDateString('tr-TR');
    };

    return (
        <div className="fade-in">
            {/* Header */}
            <div className="dashboard-header-grid" style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
                    <div style={{
                        width: '56px', height: '56px', borderRadius: '16px',
                        background: primaryColor + '10', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        border: '1px solid ' + primaryColor + '20',
                    }}>
                        {tenantData?.theme?.logoUrl ? (
                            <img src={tenantData.theme.logoUrl} alt="Logo" style={{ width: '40px', height: '40px', objectFit: 'contain', borderRadius: '8px' }} />
                        ) : (
                            <Activity size={28} color={primaryColor} />
                        )}
                    </div>
                    <div>
                        <p style={{ color: 'var(--text-muted)', fontWeight: 600, fontSize: '0.9rem', marginBottom: '2px' }}>{greeting} 👋</p>
                        <h1 style={{ fontSize: '2.25rem', fontWeight: 900, letterSpacing: '-0.02em', marginBottom: '0.25rem' }}>{tenantData?.name || t('dashboard.welcome')}</h1>
                    </div>
                </div>

                {/* Live Clock */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: '1rem',
                    padding: '0.75rem 1.25rem', borderRadius: '16px',
                    background: 'var(--bg-card)', border: '1px solid var(--border)',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.03)',
                    position: 'relative', overflow: 'hidden'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                            width: '40px', height: '40px', borderRadius: '10px',
                            background: 'rgba(var(--primary-rgb), 0.08)',
                            color: 'var(--primary)', display: 'flex',
                            alignItems: 'center', justifyContent: 'center'
                        }}>
                            <Clock size={20} />
                        </div>
                        <div>
                            <div style={{
                                fontSize: '1.5rem', fontWeight: 900, color: 'var(--text-main)',
                                lineHeight: 1, fontFamily: 'monospace', letterSpacing: '-0.02em'
                            }}>
                                {currentTime.toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </div>
                            <div style={{ fontSize: '0.7rem', fontWeight: 600, color: 'var(--text-muted)', marginTop: '4px' }}>
                                {currentTime.toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })} · {currentTime.toLocaleDateString('tr-TR', { weekday: 'long' })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Setup Wizard */}
            <SetupWizard
                tenantData={tenantData}
                effectiveTenantId={effectiveTenantId}
                customers={customers}
                promotions={promotions}
                notifications={notifications}
            />

            {/* Stat Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '1.5rem' }}>
                {/* Today Revenue */}
                <div className="card glass" onClick={() => navigate('/app/accounting')} style={{ padding: '1.25rem', cursor: 'pointer', position: 'relative', overflow: 'hidden', transition: 'all 0.2s' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#10b981' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Günün Cirosu</span>
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#10b98115', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <TrendingUp size={18} />
                        </div>
                    </div>
                    <div style={{ fontSize: '1.6rem', fontWeight: 900, marginBottom: '2px' }}>₺{todaySales.total.toLocaleString('tr-TR')}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#10b981' }}>{todaySales.count} sipariş</span>
                        {avgBasket > 0 && <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>· Ort: ₺{avgBasket}</span>}
                    </div>
                </div>

                {/* Customers */}
                <div className="card glass" onClick={() => navigate('/app/customers')} style={{ padding: '1.25rem', cursor: 'pointer', position: 'relative', overflow: 'hidden', transition: 'all 0.2s' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#3b82f6' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Toplam Müşteri</span>
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#3b82f615', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Users size={18} />
                        </div>
                    </div>
                    <div style={{ fontSize: '1.6rem', fontWeight: 900, marginBottom: '2px' }}>{(customers || []).length}</div>
                    {customerInsights && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {customerInsights.newCustomers > 0 && (
                                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#3b82f6', display: 'flex', alignItems: 'center', gap: '2px' }}>
                                    <UserPlus size={11} /> +{customerInsights.newCustomers} yeni
                                </span>
                            )}
                            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>· {customerInsights.activeCustomers} aktif</span>
                        </div>
                    )}
                </div>

                {/* Promotions */}
                <div className="card glass" onClick={() => navigate('/app/campaigns')} style={{ padding: '1.25rem', cursor: 'pointer', position: 'relative', overflow: 'hidden', transition: 'all 0.2s' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: '#f59e0b' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Kampanyalar</span>
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#f59e0b15', color: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Ticket size={18} />
                        </div>
                    </div>
                    <div style={{ fontSize: '1.6rem', fontWeight: 900, marginBottom: '2px' }}>{promotions.length}</div>
                    <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#f59e0b' }}>{promotions.filter(p => p.isReward).length} ödül · {promotions.filter(p => !p.isReward).length} indirim</span>
                </div>

                {/* Stock Alert */}
                <div className="card glass" onClick={() => navigate('/app/inventory')} style={{ padding: '1.25rem', cursor: 'pointer', position: 'relative', overflow: 'hidden', transition: 'all 0.2s' }}>
                    <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: lowStocks.length > 0 ? '#ef4444' : '#22c55e' }} />
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                        <span style={{ fontSize: '0.7rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Stok Durumu</span>
                        <div style={{
                            width: '36px', height: '36px', borderRadius: '10px',
                            background: lowStocks.length > 0 ? '#ef444415' : '#22c55e15',
                            color: lowStocks.length > 0 ? '#ef4444' : '#22c55e',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            {lowStocks.length > 0 ? <AlertTriangle size={18} /> : <Package size={18} />}
                        </div>
                    </div>
                    <div style={{ fontSize: '1.6rem', fontWeight: 900, marginBottom: '2px', color: lowStocks.length > 0 ? '#ef4444' : 'var(--text-main)' }}>{lowStocks.length}</div>
                    <span style={{ fontSize: '0.72rem', fontWeight: 600, color: lowStocks.length > 0 ? '#ef4444' : '#22c55e' }}>
                        {lowStocks.length > 0 ? 'Kritik seviyede ürün' : 'Tüm stoklar yeterli'}
                    </span>
                </div>
            </div>

            {/* QR Analytics */}
            <div className="card glass" style={{ padding: '1.5rem', marginBottom: '1.5rem' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem', flexWrap: 'wrap', gap: '0.75rem' }}>
                    <h3 style={{ fontWeight: 700, fontSize: '0.95rem', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <QrCode size={18} style={{ color: primaryColor }} /> QR Etkileşim Analizi
                    </h3>
                    <div style={{ display: 'flex', gap: '6px', background: 'var(--bg-main)', padding: '4px', borderRadius: '10px', border: '1px solid var(--border)' }}>
                        {[
                            { key: 'today', label: 'Bugün' },
                            { key: 'week', label: '7 Gün' },
                            { key: 'month', label: '30 Gün' }
                        ].map(p => (
                            <button
                                key={p.key}
                                onClick={() => setQrPeriod(p.key)}
                                style={{
                                    padding: '6px 14px', borderRadius: '8px', border: 'none',
                                    background: qrPeriod === p.key ? primaryColor : 'transparent',
                                    color: qrPeriod === p.key ? 'white' : 'var(--text-muted)',
                                    fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                            >
                                {p.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '1rem', marginBottom: '1.25rem' }}>
                    <div style={{ padding: '1rem', background: 'var(--bg-main)', borderRadius: '12px', border: '1px solid var(--border)', textAlign: 'center' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: `${primaryColor}12`, color: primaryColor, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                            <ScanLine size={18} />
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 900 }}>{qrStats.totalScans}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600, marginTop: '2px' }}>Toplam Tarama</div>
                    </div>
                    <div style={{ padding: '1rem', background: 'var(--bg-main)', borderRadius: '12px', border: '1px solid var(--border)', textAlign: 'center' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#3b82f612', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                            <UserCheck size={18} />
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#3b82f6' }}>{qrStats.newMembers}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600, marginTop: '2px' }}>Yeni Üye</div>
                    </div>
                    <div style={{ padding: '1rem', background: 'var(--bg-main)', borderRadius: '12px', border: '1px solid var(--border)', textAlign: 'center' }}>
                        <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: '#10b98112', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}>
                            <TrendingUp size={18} />
                        </div>
                        <div style={{ fontSize: '1.5rem', fontWeight: 900, color: '#10b981' }}>%{qrStats.conversionRate}</div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600, marginTop: '2px' }}>Dönüşüm Oranı</div>
                    </div>
                </div>

                {/* Top Scanners */}
                {qrStats.topCustomers.length > 0 && (
                    <div>
                        <h4 style={{ fontWeight: 700, fontSize: '0.82rem', margin: '0 0 0.75rem 0', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Star size={14} /> En Çok Taratan Müşteriler
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {qrStats.topCustomers.map((c, idx) => (
                                <div key={c.id} style={{
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                    padding: '8px 12px', background: idx === 0 ? `${primaryColor}08` : 'var(--bg-main)',
                                    borderRadius: '10px', border: '1px solid var(--border)',
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                        <div style={{
                                            width: '24px', height: '24px', borderRadius: '7px',
                                            background: idx === 0 ? primaryColor : idx === 1 ? '#8b5cf6' : idx === 2 ? '#f59e0b' : 'var(--border)',
                                            color: idx < 3 ? 'white' : 'var(--text-muted)',
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            fontSize: '0.65rem', fontWeight: 800,
                                        }}>
                                            {idx + 1}
                                        </div>
                                        <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>{anonymizeName(c.name)}</span>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '0.82rem', fontWeight: 800, color: primaryColor }}>{c.scans} tarama</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {qrStats.totalScans === 0 && (
                    <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)' }}>
                        <QrCode size={40} style={{ opacity: 0.12, marginBottom: '8px' }} />
                        <div style={{ fontSize: '0.85rem' }}>Bu dönemde QR tarama verisi bulunamadı</div>
                    </div>
                )}
            </div>

            {/* Main Grid */}
            <div className="dashboard-grid">
                {/* Left Column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                    {/* Performance Chart */}
                    <div className="card glass" style={{ padding: '1.5rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                            <h3 style={{ fontWeight: 700, fontSize: '0.95rem', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <BarChart3 size={18} style={{ color: primaryColor }} /> Bugünkü Satış Seyri
                            </h3>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: primaryColor }} />
                                    <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', fontWeight: 600 }}>Saatlik Ciro</span>
                                </div>
                            </div>
                        </div>
                        <div style={{ height: '240px', width: '100%' }}>
                            {orders.length > 0 ? (
                                <Line data={chartData} options={{
                                    responsive: true, maintainAspectRatio: false,
                                    plugins: { legend: { display: false }, tooltip: { callbacks: { label: ctx => `₺${ctx.parsed.y.toLocaleString('tr-TR')}` } } },
                                    scales: {
                                        y: { beginAtZero: true, grid: { color: 'rgba(0,0,0,0.04)' }, ticks: { callback: v => `₺${v}`, font: { size: 10 } } },
                                        x: { grid: { display: false }, ticks: { font: { size: 10 }, maxRotation: 0 } }
                                    },
                                }} />
                            ) : (
                                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '12px' }}>
                                    <BarChart3 size={40} style={{ color: 'var(--text-muted)', opacity: 0.15 }} />
                                    <span style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Bugün henüz satış verisi yok</span>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Recent Orders */}
                    <div className="card glass" style={{ padding: 0, overflow: 'hidden' }}>
                        <div style={{ padding: '1.25rem 1.5rem', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <h3 style={{ fontWeight: 700, fontSize: '0.95rem', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Receipt size={16} style={{ color: primaryColor }} /> Son Siparişler
                            </h3>
                            <button onClick={() => navigate('/app/orders')} style={{
                                background: 'none', border: 'none', color: primaryColor,
                                fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', gap: '4px',
                            }}>
                                Tümü <ChevronRight size={14} />
                            </button>
                        </div>
                        <div>
                            {orders.length === 0 ? (
                                <div style={{ padding: '2.5rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                                    <ShoppingBag size={32} style={{ opacity: 0.15, marginBottom: '8px' }} />
                                    <div style={{ fontSize: '0.85rem' }}>Bugün henüz sipariş alınmadı</div>
                                </div>
                            ) : (
                                orders.slice(0, 5).map((order, idx) => (
                                    <div key={order.id} style={{
                                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                        padding: '0.85rem 1.5rem',
                                        borderBottom: idx < Math.min(orders.length, 5) - 1 ? '1px solid var(--border)' : 'none',
                                        transition: 'background 0.15s',
                                    }}
                                        onMouseOver={e => e.currentTarget.style.background = 'rgba(var(--primary-rgb), 0.02)'}
                                        onMouseOut={e => e.currentTarget.style.background = 'transparent'}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{
                                                width: '38px', height: '38px', borderRadius: '10px',
                                                background: '#10b98110', display: 'flex',
                                                alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <ShoppingBag size={16} style={{ color: '#10b981' }} />
                                            </div>
                                            <div>
                                                <div style={{ fontWeight: 600, fontSize: '0.85rem' }}>{anonymizeName(order.customerName) || 'Misafir'}</div>
                                                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                                                    {order.items?.map(i => i.name).join(', ').slice(0, 40) || 'Ürün bilgisi yok'}
                                                    {(order.items?.map(i => i.name).join(', ') || '').length > 40 ? '...' : ''}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ textAlign: 'right' }}>
                                            <div style={{ fontWeight: 800, fontSize: '0.9rem', color: primaryColor }}>₺{order.totalAmount?.toLocaleString('tr-TR')}</div>
                                            <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>
                                                {new Date(order.timestamp).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Two Column: Promotions + Stock */}
                    <div className="dashboard-inner-grid-2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                        {/* Active Promotions */}
                        <div className="card glass" style={{ padding: '1.25rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <h4 style={{ fontWeight: 700, fontSize: '0.9rem', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Ticket size={16} style={{ color: '#f59e0b' }} /> Kampanyalar
                                </h4>
                                <button onClick={() => navigate('/app/campaigns')} style={{
                                    background: 'none', border: 'none', color: primaryColor,
                                    fontWeight: 600, fontSize: '0.72rem', cursor: 'pointer',
                                }}>Yönet</button>
                            </div>
                            {promotions.length === 0 ? (
                                <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>Aktif kampanya yok</div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {promotions.slice(0, 4).map(promo => (
                                        <div key={promo.id} style={{
                                            padding: '8px 12px', background: 'var(--bg-main)',
                                            borderRadius: '10px', border: '1px solid var(--border)',
                                            display: 'flex', alignItems: 'center', gap: '10px',
                                        }}>
                                            <div style={{
                                                padding: '5px', borderRadius: '7px',
                                                background: promo.isReward ? '#8b5cf612' : '#f59e0b12',
                                                color: promo.isReward ? '#8b5cf6' : '#f59e0b',
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                {promo.isReward ? <Gift size={13} /> : <Ticket size={13} />}
                                            </div>
                                            <div style={{ flex: 1, overflow: 'hidden' }}>
                                                <div style={{ fontSize: '0.75rem', fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{promo.title}</div>
                                                <div style={{ fontSize: '0.65rem', color: promo.isReward ? '#8b5cf6' : '#f59e0b', fontWeight: 600 }}>
                                                    {promo.isReward ? 'Ödül' : 'İndirim'}
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Critical Stocks */}
                        <div className="card glass" style={{ padding: '1.25rem' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                <h4 style={{ fontWeight: 700, fontSize: '0.9rem', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Package size={16} style={{ color: lowStocks.length > 0 ? '#ef4444' : '#22c55e' }} /> Stok Uyarıları
                                </h4>
                                <button onClick={() => navigate('/app/inventory')} style={{
                                    background: 'none', border: 'none', color: primaryColor,
                                    fontWeight: 600, fontSize: '0.72rem', cursor: 'pointer',
                                }}>Yönet</button>
                            </div>
                            {lowStocks.length === 0 ? (
                                <div style={{ padding: '1rem', textAlign: 'center' }}>
                                    <div style={{ color: '#22c55e', fontSize: '0.82rem', fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px' }}>
                                        <Package size={14} /> Tüm stoklar yeterli
                                    </div>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                    {lowStocks.slice(0, 5).map(stock => {
                                        const name = stock.id.includes('_') ? stock.id.split('_').slice(1).join('_') : stock.id;
                                        return (
                                            <div key={stock.id} style={{
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                                padding: '8px 12px', background: stock.qty === 0 ? 'rgba(239,68,68,0.04)' : 'var(--bg-main)',
                                                borderRadius: '8px', border: '1px solid var(--border)',
                                            }}>
                                                <span style={{ fontSize: '0.78rem', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>{name}</span>
                                                <span style={{
                                                    fontSize: '0.72rem', fontWeight: 800,
                                                    color: stock.qty === 0 ? '#ef4444' : '#f59e0b',
                                                    padding: '2px 8px', borderRadius: '6px',
                                                    background: stock.qty === 0 ? '#fef2f2' : '#fffbeb',
                                                }}>{stock.qty === 0 ? 'Tükendi' : `${stock.qty} kaldı`}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Right Column */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>

                    {/* Financial Summary */}
                    <div className="card glass" style={{ padding: '1.25rem', borderLeft: '3px solid var(--primary)' }}>
                        <h4 style={{ fontWeight: 700, fontSize: '0.9rem', margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Wallet size={16} style={{ color: 'var(--primary)' }} /> Günlük Mali Özet
                        </h4>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>Toplam Ciro</span>
                                <span style={{ fontWeight: 800, fontSize: '0.9rem' }}>₺{todaySales.total.toLocaleString('tr-TR')}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>Sipariş Sayısı</span>
                                <span style={{ fontWeight: 800, fontSize: '0.9rem' }}>{todaySales.count}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>Ort. Sepet</span>
                                <span style={{ fontWeight: 800, fontSize: '0.9rem' }}>₺{avgBasket.toLocaleString('tr-TR')}</span>
                            </div>
                            <div style={{ height: '1px', background: 'var(--border)', margin: '4px 0' }} />
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)', fontWeight: 500 }}>Tahmini Brüt Kâr</span>
                                <span style={{ fontWeight: 800, fontSize: '0.9rem', color: '#10b981' }}>₺{Math.round(todaySales.total * 0.4).toLocaleString('tr-TR')}</span>
                            </div>
                            <button onClick={() => navigate('/app/accounting')} style={{
                                marginTop: '6px', padding: '8px', borderRadius: '10px',
                                border: '1px solid var(--primary)', background: 'rgba(var(--primary-rgb), 0.05)',
                                color: 'var(--primary)', fontWeight: 700, fontSize: '0.78rem',
                                cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                            }}>
                                Muhasebe Paneli <ChevronRight size={14} />
                            </button>
                        </div>
                    </div>

                    {/* Notifications */}
                    <div className="card glass" style={{ padding: '1.25rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <h4 style={{ fontWeight: 700, fontSize: '0.9rem', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Bell size={16} style={{ color: primaryColor }} /> Duyurular
                            </h4>
                            <button onClick={() => navigate('/app/notifications')} style={{
                                background: 'none', border: 'none', color: primaryColor,
                                fontWeight: 600, fontSize: '0.72rem', cursor: 'pointer',
                            }}>Tümü</button>
                        </div>
                        {notifications.length === 0 ? (
                            <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                                <Bell size={24} style={{ opacity: 0.15, marginBottom: '6px' }} /><br />Duyuru yok
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {notifications.slice(0, 3).map((notif, idx) => (
                                    <div key={notif.id} style={{
                                        padding: '10px 12px', background: 'var(--bg-main)',
                                        borderRadius: '10px', border: '1px solid var(--border)',
                                    }}>
                                        <div style={{ fontWeight: 700, fontSize: '0.82rem', marginBottom: '4px' }}>{notif.title}</div>
                                        <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', lineHeight: 1.4 }}>
                                            {notif.content?.slice(0, 80)}{notif.content?.length > 80 ? '...' : ''}
                                        </div>
                                        <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: '6px' }}>
                                            {notif.createdAt ? formatTimeAgo(notif.createdAt) : ''}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Customer Insights */}
                    {customerInsights && (
                        <div className="card glass" style={{ padding: '1.25rem' }}>
                            <h4 style={{ fontWeight: 700, fontSize: '0.9rem', margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <Star size={16} style={{ color: '#f59e0b' }} /> Müşteri Özeti (30 Gün)
                            </h4>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                                <div style={{ padding: '10px', background: 'var(--bg-main)', borderRadius: '10px', border: '1px solid var(--border)', textAlign: 'center' }}>
                                    <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#3b82f6' }}>{customerInsights.newCustomers}</div>
                                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 600, marginTop: '2px' }}>Yeni Müşteri</div>
                                </div>
                                <div style={{ padding: '10px', background: 'var(--bg-main)', borderRadius: '10px', border: '1px solid var(--border)', textAlign: 'center' }}>
                                    <div style={{ fontSize: '1.1rem', fontWeight: 800, color: '#10b981' }}>{customerInsights.activeCustomers}</div>
                                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 600, marginTop: '2px' }}>Aktif Müşteri</div>
                                </div>
                                <div style={{ padding: '10px', background: 'var(--bg-main)', borderRadius: '10px', border: '1px solid var(--border)', textAlign: 'center', gridColumn: '1 / -1' }}>
                                    <div style={{ fontSize: '1.1rem', fontWeight: 800 }}>₺{customerInsights.totalSpent.toLocaleString('tr-TR')}</div>
                                    <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', fontWeight: 600, marginTop: '2px' }}>Toplam Harcama</div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Activity Feed */}
                    <div className="card glass" style={{ padding: '1.25rem' }}>
                        <h4 style={{ fontWeight: 700, fontSize: '0.9rem', margin: '0 0 1rem 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Activity size={16} style={{ color: primaryColor }} /> Son Aktiviteler
                        </h4>
                        {activities.length === 0 ? (
                            <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>Henüz aktivite yok</div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                {activities.slice(0, 6).map((activity, idx) => {
                                    const ActIcon = getActivityIcon(activity.action);
                                    const actColor = getActivityColor(activity.action);
                                    return (
                                        <div key={activity.id} style={{
                                            display: 'flex', gap: '10px', padding: '8px 4px',
                                            borderBottom: idx < Math.min(activities.length, 6) - 1 ? '1px solid var(--border)' : 'none',
                                        }}>
                                            <div style={{
                                                width: '28px', height: '28px', borderRadius: '8px', flexShrink: 0,
                                                background: `${actColor}10`, color: actColor,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            }}>
                                                <ActIcon size={13} />
                                            </div>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <div style={{ fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                    {activity.details || activity.action}
                                                </div>
                                                <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', display: 'flex', gap: '6px' }}>
                                                    <span>{activity.userEmail?.split('@')[0]}</span>
                                                    <span>·</span>
                                                    <span>{formatTimeAgo(activity.timestamp)}</span>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <style>{`
                .dashboard-grid {
                    display: grid;
                    grid-template-columns: 1.8fr 1fr;
                    gap: 1rem;
                }
                @media (max-width: 1024px) {
                    .dashboard-grid { grid-template-columns: 1fr; }
                }
                @media (max-width: 768px) {
                    .dashboard-grid { gap: 0.75rem; }
                }
                @media (max-width: 640px) {
                    .dashboard-inner-grid-2 { grid-template-columns: 1fr !important; }
                    .dashboard-header-grid { flex-direction: column !important; align-items: flex-start !important; }
                    .dashboard-header-grid h1 { font-size: 1.5rem !important; }
                }
            `}</style>
        </div>
    );
};

export default Dashboard;
